import { Suspense, useState } from 'react';
import { Route, useRouteMatch } from 'react-router';
import r from '../../../react/services/Replacement';
import * as ROUTES from '../../../routes';
import LoadingIndicatorFull from '../../components/LoadingIndicatorFull';
import NavigationTabs from '../../components/NavigationTabs';
import PageHeader from '../../components/PageHeader';
import PageStatic from '../../components/PageStatic';
import SwitchDefaultRoute from '../../components/SwitchDefaultRoute';
import SwitchErrorRoute from '../../components/SwitchErrorRoute';
import { useAppContext } from '../../hooks/useAppContext';
import { ObjectiveCycleSelectContext } from '../../hooks/useObjectiveCycleSelectContext';
import { getAngularChildRoutes } from '../../services/AngularRoute';
import { isFeatureEnabled, isFeatureEnabledForUser } from '../../services/Features';
import { hasBasicHr, isAnyAdmin } from '../../services/User';
import ActivityStreamTab from '../ActivityStream/tab';
import ObjectiveHierarchyView from '../Objectives/HierarchyView/lazy';
import ObjectiveTreeView from '../Objectives/TreeView/lazy';
import SurveyDashboard from '../Surveys/pages/SurveyDashboard/lazy';
import SurveyOverview from '../Surveys/pages/SurveyOverview/lazy';
import { useShowSurveysTab } from '../Surveys/services/useShowSurveysTab';

export const showObjectiveAnalyzeInCompanyMode = user =>
    !hasBasicHr(user) && isFeatureEnabled('FEATURE_objectiveAnalyzeViewForICs');

const Objectives = ({ match }) => {
    const { user } = useAppContext();

    const routes = [ROUTES.YOUR_COMPANY_OBJECTIVES_BROWSER];

    if (showObjectiveAnalyzeInCompanyMode(user)) {
        routes.push(ROUTES.YOUR_COMPANY_OBJECTIVES_OVERVIEW, ROUTES.YOUR_COMPANY_OBJECTIVES_SELECT_CYCLE);
    }
    const angularRoutes = getAngularChildRoutes(routes);

    if (hasBasicHr(user)) {
        angularRoutes.reverse();
    }

    return (
        <SwitchDefaultRoute
            defaultPath={match.path}
            keySuffixOverride={
                isFeatureEnabled('FEATURE_objectiveNewListView') ? `${match.path}-with-list-view` : undefined
            }
        >
            {isFeatureEnabledForUser('FEATURE_objectiveCascading', user) && (
                <Route exact path={ROUTES.YOUR_COMPANY_OBJECTIVES_CASCADE} component={ObjectiveTreeViewWithContext} />
            )}
            {angularRoutes}
            {isFeatureEnabledForUser('FEATURE_objectiveHierarchyView', user) &&
                !isFeatureEnabled('FEATURE_objectiveNewListView') && (
                    <Route
                        exact
                        path={ROUTES.YOUR_COMPANY_OBJECTIVES + '/hierarchy'}
                        component={ObjectiveHierarchyView}
                    />
                )}
        </SwitchDefaultRoute>
    );
};

function ObjectiveTreeViewWithContext() {
    // cycles will be set through sub-routes via ObjectiveCycleSelectContext.
    const [cycles, setCycles] = useState([]);
    return (
        <ObjectiveCycleSelectContext.Provider value={{ cycles, setCycles }}>
            <ObjectiveTreeView />
        </ObjectiveCycleSelectContext.Provider>
    );
}

const Feedback = () => {
    const routes = getAngularChildRoutes([
        ROUTES.YOUR_COMPANY_FEEDBACK_SELECT_CYCLE,
        ROUTES.YOUR_COMPANY_FEEDBACK_WITH_CYCLE,
    ]);

    return <SwitchErrorRoute>{routes}</SwitchErrorRoute>;
};

const Reviews = () => {
    const routes = getAngularChildRoutes([
        ROUTES.YOUR_COMPANY_REVIEWS_SELECT_CYCLE,
        ROUTES.YOUR_COMPANY_REVIEWS_WITH_CYCLE,
    ]);

    return <SwitchErrorRoute>{routes}</SwitchErrorRoute>;
};

const Messages = () => {
    const route = getAngularChildRoutes([ROUTES.YOUR_COMPANY_MESSAGES]);

    return <SwitchErrorRoute>{route}</SwitchErrorRoute>;
};

const Directory = () => {
    const route = getAngularChildRoutes([ROUTES.YOUR_COMPANY_USERS]);

    return <SwitchErrorRoute>{route}</SwitchErrorRoute>;
};

function hasAnyCycleAdminRights(user, cycleType) {
    const rights = user.cycleAdminRights || {};
    const forCycle = rights[cycleType] || [];
    return !!forCycle.length;
}

export default function CompanyPage() {
    const match = useRouteMatch();
    const { user } = useAppContext();
    const showSurveys = useShowSurveysTab();

    const showFeedback = !hasBasicHr(user) && hasAnyCycleAdminRights(user, 'feedbackCycles');

    const showReviews = !hasBasicHr(user) && hasAnyCycleAdminRights(user, 'reviewCycles');

    const tabs = [
        {
            label: 'Activities',
            link: ROUTES.YOUR_COMPANY_ACTIVITY,
            visible: true,
            trackingLabel: 'activities',
        },
        {
            label: r('surveys.plural.big'),
            link: ROUTES.YOUR_COMPANY_SURVEYS,
            visible: showSurveys,
            feature: 'surveys',
            trackingLabel: 'surveys',
        },
        {
            label: r('objective.plural.big'),
            link: ROUTES.YOUR_COMPANY_OBJECTIVES,
            visible: !isFeatureEnabled('FEATURE_layoutRefresh'),
            feature: 'objective',
            trackingLabel: 'objectives',
        },
        {
            label: r('fr.review.singular.title'),
            link: ROUTES.YOUR_COMPANY_FEEDBACK_SELECT_CYCLE,
            visible: showFeedback,
            feature: 'feedback',
            trackingLabel: 'feedback',
        },
        {
            label: r('pr.plural.short'),
            link: ROUTES.YOUR_COMPANY_REVIEWS_SELECT_CYCLE,
            visible: showReviews,
            feature: 'review',
            trackingLabel: 'reviews',
        },
        {
            label: r('messages.menu.title'),
            link: ROUTES.YOUR_COMPANY_MESSAGES,
            visible: isFeatureEnabled('message'),
            trackingLabel: 'messages',
        },
        {
            label: 'Directory',
            link: ROUTES.YOUR_COMPANY_USERS,
            visible: !isAnyAdmin(user),
            trackingLabel: 'directory',
        },
    ];
    return (
        <div>
            <PageStatic className="no-print">
                <PageHeader title="Your Company" />
            </PageStatic>
            <NavigationTabs className="no-print" tabs={tabs} baseUrl={match.url} trackingCategory="company page" />
            <Suspense fallback={<LoadingIndicatorFull />}>
                <SwitchDefaultRoute defaultPath={match.path}>
                    <Route exact path={ROUTES.YOUR_COMPANY_ACTIVITY} component={ActivityStreamTab} />
                    {!isFeatureEnabled('FEATURE_layoutRefresh') && (
                        <Route path={ROUTES.YOUR_COMPANY_OBJECTIVES} component={Objectives} />
                    )}
                    {showReviews && <Route path={ROUTES.YOUR_COMPANY_REVIEWS_SELECT_CYCLE} component={Reviews} />}
                    {showFeedback && <Route path={ROUTES.YOUR_COMPANY_FEEDBACK_SELECT_CYCLE} component={Feedback} />}
                    <Route exact path={ROUTES.YOUR_COMPANY_SURVEYS} component={SurveyOverview} />
                    {isFeatureEnabled('surveys') && showSurveys && (
                        <Route
                            path={ROUTES.YOUR_COMPANY_SURVEYS_WITH_SURVEY}
                            render={props => (
                                // We need the key to re-initialize the page upon jumping to a different survey.
                                <SurveyDashboard
                                    key={props.match.params.surveyId}
                                    surveyId={props.match.params.surveyId}
                                />
                            )}
                        />
                    )}
                    <Route exact path={ROUTES.YOUR_COMPANY_MESSAGES} component={Messages} />
                    <Route path={ROUTES.YOUR_COMPANY_USERS} component={Directory} />
                </SwitchDefaultRoute>
            </Suspense>
        </div>
    );
}
